import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Download, X, ChevronDown } from "lucide-react";
import html2canvas from "html2canvas";
import axios from "axios";
import NavBar from "@/components/NavBar";
import { useToast } from "@/hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelectField = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
      {label}
    </label>
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-emerald-400 focus:outline-none appearance-none text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
    </div>
  </div>
);

const PedidosPage = () => {
  const { toast } = useToast();
  const [sedes, setSedes] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSede, setSelectedSede] = useState("");
  const [selectedZona, setSelectedZona] = useState("");
  const [selectedFecha, setSelectedFecha] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [quantities, setQuantities] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [filterByZona, setFilterByZona] = useState(false);
  const ticketRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sedesRes, zonasRes, productsRes] = await Promise.all([
          axios.get(`${API}/sedes`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/zonas`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/productos/pedidos`, { params: { skip: 0, limit: 1000 } }),
        ]);
        setSedes(sedesRes.data.items ?? sedesRes.data);
        setZonas(zonasRes.data.items ?? zonasRes.data);
        setProducts(productsRes.data.items ?? productsRes.data);
        const sedes = sedesRes.data.items ?? sedesRes.data;
        const zonas = zonasRes.data.items ?? zonasRes.data;
        if (sedes.length > 0) setSelectedSede(sedes[0].id);
        if (zonas.length > 0) setSelectedZona(zonas[0].id);
      } catch (err) {
        console.error("Error cargando datos:", err);
        toast({
          title: "Error al cargar datos",
          description: err.response?.status === 500
            ? "Error del servidor. Intenta más tarde."
            : "No se pudieron cargar los datos. Verifica tu conexión.",
          variant: "destructive",
        });
      }
    };
    fetchData();
  }, []);

  const updateQuantity = useCallback((productId, field, value) => {
    const normalizedValue = value === "" ? "" : value.replace(/[^0-9]/g, "");
    setQuantities((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: normalizedValue === "" ? "" : Number(normalizedValue),
      },
    }));
  }, []);

  // Filtro por zona (activo solo cuando filterByZona está activado)
  const zonaProducts = !filterByZona
    ? products
    : selectedZona
    ? products.filter((p) => p.zona_id === selectedZona)
    : [];

  // Filtro adicional por búsqueda de texto
  const filteredProducts = zonaProducts.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Para exportar: todos los de la zona con pedida > 0 (sin filtro de búsqueda)
  const exportProducts = zonaProducts.filter(
    (p) => Number(quantities[p.id]?.pedida || 0) > 0
  );

  const sedeName = sedes.find((s) => s.id === selectedSede)?.name || "—";
  const zonaName = zonas.find((z) => z.id === selectedZona)?.name || "—";
  const formattedDate = selectedFecha
    ? new Date(selectedFecha + "T12:00:00").toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

  const exportTicket = async () => {
    if (exportProducts.length === 0) return;
    setIsExporting(true);
    await new Promise((r) => setTimeout(r, 150));
    try {
      const canvas = await html2canvas(ticketRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      const filename = `pedido_${selectedFecha || "fecha"}_${sedeName}.png`.replace(/\s+/g, "_");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Error generando ticket:", err);
      toast({
        title: "Error al exportar",
        description: "No se pudo generar el ticket. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="bg-neutral-950 flex flex-col max-w-md mx-auto relative"
      style={{ fontFamily: "'Outfit', sans-serif", minHeight: "100dvh" }}
    >
      <NavBar title="Pedidos" subtitle="Registro de pedido" />

      {/* Header form */}
      <div className="px-4 pt-4 pb-3 space-y-3 border-b border-neutral-800 bg-neutral-950">
        <SelectField
          label="Sede"
          value={selectedSede}
          onChange={setSelectedSede}
          options={sedes}
          placeholder="— Seleccionar sede —"
          testId="select-sede"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
              Fecha
            </label>
            <input
              data-testid="input-fecha"
              type="date"
              value={selectedFecha}
              onChange={(e) => setSelectedFecha(e.target.value)}
              className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-neutral-50 focus:border-emerald-400 focus:outline-none text-sm"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500">
                Zona
              </label>
              <button
                data-testid="toggle-filter-zona"
                onClick={() => setFilterByZona((v) => !v)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  filterByZona
                    ? "bg-emerald-400/15 text-emerald-400"
                    : "bg-neutral-800 text-neutral-500"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full inline-block transition-colors ${
                    filterByZona ? "bg-emerald-400" : "bg-neutral-600"
                  }`}
                />
                Filtrar
              </button>
            </div>
            <div className="relative">
              <select
                data-testid="select-zona"
                value={selectedZona}
                onChange={(e) => setSelectedZona(e.target.value)}
                className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-emerald-400 focus:outline-none appearance-none text-sm"
              >
                <option value="">— Zona —</option>
                {zonas.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-950">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            data-testid="input-search-product"
            type="text"
            placeholder="Buscar producto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 bg-neutral-900 border border-neutral-800 rounded-md pl-9 pr-9 text-neutral-50 placeholder:text-neutral-600 focus:border-emerald-400 focus:outline-none text-sm"
          />
          {searchQuery && (
            <button
              data-testid="btn-clear-search"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-neutral-500" />
            </button>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className="px-4 py-2 flex items-center bg-neutral-950 border-b border-neutral-800">
        <div className="flex-1 text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Producto
        </div>
        <div className="w-14 text-center text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Exist.
        </div>
        <div className="w-14 text-center text-xs font-semibold tracking-widest uppercase text-neutral-500 ml-2">
          Pedido
        </div>
      </div>

      {/* Product list */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-28">
        {products.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos configurados.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Ve a Administración para agregar productos de pedidos.
            </p>
          </div>
        ) : filterByZona && !selectedZona ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Selecciona una zona</p>
            <p className="text-neutral-600 text-xs mt-1">
              Elige una zona para ver sus productos.
            </p>
          </div>
        ) : filterByZona && zonaProducts.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos para esta zona.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Agrega productos asignados a esta zona en Administración.
            </p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="px-4 py-10 text-center text-neutral-500 text-sm">
            No se encontraron productos con ese nombre.
          </div>
        ) : (
          filteredProducts.map((product) => {
            const pedida = Number(quantities[product.id]?.pedida || 0);
            const hasPedida = pedida > 0;
            return (
              <div
                key={product.id}
                data-testid={`product-row-${product.id}`}
                className={`flex items-center px-4 py-3 border-b border-neutral-800 transition-colors duration-150 ${
                  hasPedida ? "bg-emerald-950/30" : ""
                }`}
              >
                <div className="flex-1 min-w-0 pr-2">
                  <div
                    className={`text-sm font-medium truncate ${
                      hasPedida ? "text-emerald-400" : "text-neutral-50"
                    }`}
                  >
                    {product.name}
                  </div>
                </div>
                <input
                  data-testid={`input-existente-${product.id}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  enterKeyHint="next"
                  min="0"
                  step="1"
                  value={quantities[product.id]?.existente ?? ""}
                  onChange={(e) =>
                    updateQuantity(product.id, "existente", e.target.value)
                  }
                  placeholder="0"
                  className="w-14 h-11 bg-neutral-800 border border-neutral-700 rounded text-center text-neutral-50 text-sm font-mono-data focus:border-neutral-600 focus:outline-none"
                />
                <div className="w-2" />
                <input
                  data-testid={`input-pedida-${product.id}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  enterKeyHint="done"
                  min="0"
                  step="1"
                  value={quantities[product.id]?.pedida ?? ""}
                  onChange={(e) =>
                    updateQuantity(product.id, "pedida", e.target.value)
                  }
                  placeholder="0"
                  className={`w-14 h-11 border rounded text-center text-sm font-mono-data focus:outline-none transition-colors duration-150 ${
                    hasPedida
                      ? "bg-emerald-950 border-emerald-400/60 text-emerald-400 focus:border-emerald-400"
                      : "bg-neutral-800 border-neutral-700 text-neutral-50 focus:border-neutral-600"
                  }`}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Export footer */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-3 bg-neutral-950/95 backdrop-blur border-t border-neutral-800 max-w-md mx-auto w-full z-40"
        style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        <button
          data-testid="export-ticket-button"
          onClick={exportTicket}
          disabled={exportProducts.length === 0 || isExporting}
          className={`w-full h-14 rounded-md font-semibold text-base flex items-center justify-center gap-2 transition-colors duration-150 ${
            exportProducts.length > 0 && !isExporting
              ? "bg-emerald-400 text-neutral-950 active:bg-emerald-300"
              : "bg-neutral-800 text-neutral-600 cursor-not-allowed"
          }`}
        >
          <Download className="w-5 h-5" />
          {isExporting
            ? "Generando imagen..."
            : exportProducts.length > 0
            ? `Exportar ticket (${exportProducts.length} producto${exportProducts.length !== 1 ? "s" : ""})`
            : "Ingresa cantidades pedidas"}
        </button>
      </div>

      {/* Hidden ticket for html2canvas — absolute para evitar glitches en iOS al abrir teclado */}
      <div
        ref={ticketRef}
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          backgroundColor: "#ffffff",
          color: "#000000",
          width: "420px",
          padding: "20px 18px",
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: "12px",
          lineHeight: "1.6",
        }}
      >
        {/* Info header */}
        <div
          style={{
            marginBottom: "14px",
            paddingBottom: "10px",
            borderBottom: "2px solid #000000",
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            <span style={{ fontWeight: "bold", width: "80px" }}>Fecha:</span>
            <span>{formattedDate}</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <span style={{ fontWeight: "bold", width: "80px" }}>Sede:</span>
            <span>{sedeName}</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <span style={{ fontWeight: "bold", width: "80px" }}>Zona:</span>
            <span>{zonaName}</span>
          </div>
        </div>

        {/* Column headers */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 58px 68px",
            fontWeight: "bold",
            fontSize: "11px",
            paddingBottom: "6px",
            borderBottom: "1px solid #000000",
            marginBottom: "4px",
            gap: "4px",
          }}
        >
          <div>PRODUCTO</div>
          <div style={{ textAlign: "center" }}>EXIST.</div>
          <div style={{ textAlign: "center" }}>PEDIDO</div>
        </div>

        {/* Product rows */}
        {exportProducts.map((p, i) => (
          <div
            key={p.id}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 58px 68px",
              padding: "5px 0",
              borderBottom: "1px solid #e0e0e0",
              fontSize: "12px",
              backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#ffffff",
              gap: "4px",
            }}
          >
            <div style={{ paddingLeft: "2px" }}>{p.name}</div>
            <div style={{ textAlign: "center" }}>
              {quantities[p.id]?.existente || 0}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold" }}>
              {quantities[p.id]?.pedida || 0}
            </div>
          </div>
        ))}

        {/* Total */}
        <div
          style={{
            marginTop: "10px",
            paddingTop: "8px",
            borderTop: "2px solid #000000",
            fontWeight: "bold",
            fontSize: "12px",
          }}
        >
          Total: {exportProducts.length} producto
          {exportProducts.length !== 1 ? "s" : ""}
        </div>
      </div>
    </div>
  );
};

export default PedidosPage;
